import { Dialog, DialogWindow, useDialogManager } from '../../widgets/dialog';
import { FormControl } from '../../widgets/formControl';
import { showErrorNotification, showInfoNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import React, { FC, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { graphql, useMutation } from 'react-relay';
import config from 'src/shared/config';
import { InputWithValidation } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import { validateRequired, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { salesChannelsActive$data } from './__generated__/salesChannelsActive.graphql';
import { storeUrlSalesChannelDialogSaveMutation } from './__generated__/storeUrlSalesChannelDialogSaveMutation.graphql';

interface StoreUrlSalesChannelDialogProps {
    salesChannel: salesChannelsActive$data['salesChannels'][0];
}

const CustomWindow = styled(DialogWindow)`
    min-height: 350px;
`;

const StoreUrlSalesChannelDialog: FC<StoreUrlSalesChannelDialogProps> = ({ salesChannel }) => {
    const { closeDialog, currentDialogId } = useDialogManager();
    const [storeUrl, setStoreUrl] = useState(salesChannel.ecommerceSettings?.remoteUrl ?? '');
    const [recaptchaToken, setRecaptchaToken] = useState('');

    const [save, isPending] = useMutation<storeUrlSalesChannelDialogSaveMutation>(graphql`
        mutation storeUrlSalesChannelDialogSaveMutation($input: EcommerceSettingsInput!) {
            saveEcommerceSettings(settingsInput: $input) {
                ecommerceID
            }
        }
    `);

    const handleSave = async () => {
        showInfoNotification({
            message: T._('Saving...', 'Admin')
        });

        save({
            variables: {
                input: {
                    ecommerceGlobalID: salesChannel.ecommerceSettings!.ecommerceGlobalID!,
                    salesChannelID: salesChannel.id,
                    recaptchaToken,
                    remoteUrl: storeUrl
                }
            },
            onCompleted: () => {
                showSuccessNotification({
                    message: T._('Saved', 'Admin')
                });
                closeDialog(currentDialogId);
            },
            onError: (error) => {
                showErrorNotification({
                    message: T._('An error occurred while saving.', 'Admin')
                });
            }
        });
    };

    return (
        <Dialog
            title={T._('Change store URL', 'Admin')}
            windowDecorator={CustomWindow}
            buttons={[
                {
                    disabled: isPending || !recaptchaToken,
                    label: T._('Confirm', 'Admin'),
                    onClick: () => {
                        handleSave();
                    }
                }
            ]}
            onClose={() => closeDialog(currentDialogId)}
        >
            <ValidationStateProvider>
                <FormControl label={T._('Store URL', 'Admin')}>
                    <InputWithValidation
                        value={storeUrl}
                        onChange={(e) => setStoreUrl(e.target.value)}
                        validate={validateRequired}
                        validationKey={'storeURL'}
                        message={T._('This field is required.', 'Admin')}
                    />
                </FormControl>
                <FormControl>
                    <ReCAPTCHA sitekey={config.googleRecaptchaSitekey} theme={'light'} size={'normal'} onChange={(e) => e && setRecaptchaToken(e)}></ReCAPTCHA>
                </FormControl>
            </ValidationStateProvider>
        </Dialog>
    );
};

export default StoreUrlSalesChannelDialog;
